import { Trash2 } from "lucide-react";
import { memo, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import { UnloadingDirectionDTOTariffStation, useGetRailstations, useLoadRoad } from "@/common/api";
import {
  Button,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SearchableCombobox,
  Select,
  SelectContent,
  SelectItem,
} from "@/common/components";
import { TariffLabelsEnum } from "@/common/model";

const trafficOptions = [
  { value: UnloadingDirectionDTOTariffStation.WITH_20_VAT, label: TariffLabelsEnum.With20Vat },
  { value: UnloadingDirectionDTOTariffStation.WITH_0_VAT, label: TariffLabelsEnum.With0Vat },
  { value: UnloadingDirectionDTOTariffStation.WITH_OUT_VAT, label: TariffLabelsEnum.WithOutVat },
  { value: UnloadingDirectionDTOTariffStation.COEFFICIENT, label: TariffLabelsEnum.Coefficient },
];

const LIMIT = 100;
const PAGE = 1;

interface Props {
  index: number;
  onRemoveDirection: (index: number) => void;
}

export const UnloadingDirectionItem = memo<Props>(({ index, onRemoveDirection }) => {
  const { control, register, setValue, watch, trigger } = useFormContext();

  const watchStation = watch(`unloadingDirection.${index}.unloadingStation`);
  const watchRoad = watch(`unloadingDirection.${index}.unloadingRoad`);
  const watchCountWagons = watch(`unloadingDirection.${index}.countWagons`);

  // Автоматически триггерим валидацию totalWagons при изменении countWagons
  useEffect(() => {
    trigger("totalWagons");
  }, [watchCountWagons, trigger]);

  const [stationSearch, setStationSearch] = useState("");
  const [stationOptions, setStationOptions] = useState<Record<"label" | "value", string>[]>([]);
  const [roadSearch, setRoadSearch] = useState("");
  const [roadOptions, setRoadOptions] = useState<Record<"label" | "value", string>[]>([]);

  const { data: stationsData } = useGetRailstations({
    name: stationSearch,
    code: watchStation,
    page: PAGE,
    limit: LIMIT,
  });

  const { data: roadData } = useLoadRoad({
    name: roadSearch,
    code: watchRoad,
    page: PAGE,
    limit: LIMIT,
  });

  useEffect(() => {
    setStationOptions(
      stationsData?.data.payload?.items?.map(({ name, code, road }) => {
        const roadName = road ?? "";
        const label = roadName ? `${name ?? ""} (${roadName})` : (name ?? "");
        return {
          label,
          value: code ?? "",
        };
      }) ?? [],
    );
  }, [stationsData]);

  useEffect(() => {
    setRoadOptions(
      roadData?.data.payload?.items?.map(({ name, code, fullName }) => ({
        label: fullName ?? name ?? "",
        value: code ?? "",
      })) ?? [],
    );
  }, [roadData]);

  return (
    <div className="space-y-5 mt-5 ">
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 border-t border-Border-gray pt-5">
        <FormField
          control={control}
          name={`unloadingDirection.${index}.unloadingStation` as const}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Станция выгрузки №{index + 1}</FormLabel>
              <SearchableCombobox
                options={stationOptions}
                value={watchStation}
                onValueChange={(value) => {
                  field.onChange(value);
                  const item = stationsData?.data.payload?.items?.find(
                    (item) => item.code === value,
                  );

                  setValue(`unloadingDirection.${index}.unloadingStation`, value);
                  setValue(`unloadingDirection.${index}.stationCode`, value);
                  setValue(`unloadingDirection.${index}.unloadingRoad`, item?.fullRoad?.code ?? "");
                  setValue(`unloadingDirection.${index}.roadCode`, item?.fullRoad?.code ?? "");
                  trigger(`unloadingDirection.${index}`);

                  setStationSearch("");
                  setRoadSearch("");
                }}
                onSearchChange={(value) => {
                  setValue(`unloadingDirection.${index}.unloadingStation`, "");
                  setValue(`unloadingDirection.${index}.stationCode`, "");
                  setValue(`unloadingDirection.${index}.unloadingRoad`, "");
                  setValue(`unloadingDirection.${index}.roadCode`, "");

                  setStationSearch(value);
                  setRoadSearch("");
                }}
                placeholder="Выберите станцию выгрузки"
                searchPlaceholder="Поиск станции..."
                emptyText="Станция не найдена"
                className="w-full "
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`unloadingDirection.${index}.unloadingRoad` as const}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Дорога выгрузки *</FormLabel>
              <SearchableCombobox
                options={roadOptions}
                value={watchRoad}
                onValueChange={(value) => {
                  field.onChange(value);
                  setValue(`unloadingDirection.${index}.unloadingRoad`, value);
                  setValue(`unloadingDirection.${index}.roadCode`, value);
                  setValue(`unloadingDirection.${index}.unloadingStation`, "");
                  setRoadSearch("");
                }}
                onSearchChange={(value) => {
                  setValue(`unloadingDirection.${index}.unloadingRoad`, "");
                  setValue(`unloadingDirection.${index}.roadCode`, "");
                  setRoadSearch(value);
                }}
                placeholder="Выберите дорогу выгрузки"
                searchPlaceholder="Поиск дороги..."
                emptyText="Дорога не найдена"
                className="w-full"
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4 w-full">
        <FormField
          control={control}
          name={`unloadingDirection.${index}.stationCode` as const}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Код станции</FormLabel>
              <Input {...field} placeholder="Код станции" disabled />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormItem>
          <FormLabel>Код дороги</FormLabel>
          <Input
            {...register(`unloadingDirection.${index}.roadCode` as const)}
            placeholder="Код дороги"
            className="w-full"
            disabled
          />
          <FormMessage />
        </FormItem>
        <FormField
          control={control}
          name={`unloadingDirection.${index}.tariffStation`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Тип тарифа *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value} placeholder="Тип">
                <SelectContent>
                  {trafficOptions.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`unloadingDirection.${index}.countWagons`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="whitespace-nowrap">Количество вагонов</FormLabel>
              <Input {...field} type="number" placeholder="Кол-во" />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="w-full mt-3 md:mt-4 flex items-end justify-center md:justify-end gap-2 self-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onRemoveDirection(index)}
          className=""
        >
          <Trash2 className="hidden md:block h-4 w-4" />
          Удалить направление <span className="hidden md:block">выгрузки</span>
        </Button>
      </div>
    </div>
  );
});
